/**
 * /legal/privacy — Privacy policy. POPIA-compliant.
 *
 * B6 (white-label public POPIA): on a tenant subdomain ({slug}.elreign.io)
 * the Information Officer block shows the TENANT's designated IO (name + the
 * alias/role email surfaced by the public officers endpoint), as POPIA s55
 * requires the responsible party's IO to be reachable from its privacy
 * notice. On apex elreign.io / app*.elreign.io / an unknown slug / a tenant
 * with no designated officer, it falls back to Elreign's platform Information
 * Officer (Paul Prinsloo / info@elreign.io) — unchanged from before.
 */

import { createFileRoute } from '@tanstack/react-router';
import { LegalLayout } from '../../components/layouts/legal-layout';
import { resolveTenantSlugFromHost } from '../../lib/tenant';
import { useTenantPopiaOfficersBySlug } from '../../lib/api';
import { displayPersonName } from '../../lib/format/person-name';

export const Route = createFileRoute('/legal/privacy')({
  component: PrivacyPage,
});

// ── Elreign platform default Information Officer ──────────────────────────────
// Rendered on apex / app / unknown-slug / no-designated-officer. Unchanged
// from the pre-B6 hard-coded block.
const ELREIGN_DEFAULT_IO = {
  name: 'Paul Prinsloo',
  email: 'info@elreign.io',
} as const;

const TOC = [
  { anchor: 'who-we-are',         label: 'Who we are' },
  { anchor: 'what-data',          label: 'What personal information we hold' },
  { anchor: 'why-we-hold',        label: 'Why we hold it' },
  { anchor: 'retention',          label: 'How long we keep it' },
  { anchor: 'your-rights',        label: 'Your rights under POPIA' },
  { anchor: 'information-officer', label: 'Information Officer' },
  { anchor: 'complaints',         label: 'Complaints to the Information Regulator' },
];

function PrivacyPage() {
  // B6: resolve the tenant slug from the host (same resolver branding uses in
  // __root.tsx). null on apex / app / local-dev → the hook is disabled and we
  // render the Elreign default officer.
  const slug = resolveTenantSlugFromHost(
    typeof window !== 'undefined' ? window.location.host : '',
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : undefined,
  );
  const officersQ = useTenantPopiaOfficersBySlug(slug);
  const tenantIo = officersQ.data?.officers.io ?? null;

  // Render the tenant's designated IO when on a tenant subdomain AND an
  // officer is designated; otherwise the Elreign platform default. We never
  // invent a tenant postal address (the public endpoint exposes none) — the
  // "TBD on registration" line is preserved for both cases.
  const ioName = displayPersonName(tenantIo?.displayName) || ELREIGN_DEFAULT_IO.name;
  const ioEmail = tenantIo?.email ?? ELREIGN_DEFAULT_IO.email;

  return (
    <LegalLayout heading="Privacy policy." lastUpdated="7 May 2026" toc={TOC}>
      <h2 id="who-we-are">Who we are</h2>
      <p>
        Elreign Legacy (Pty) Ltd, trading as <strong>Elreign</strong> (<strong>we</strong>, <strong>us</strong>), is a
        South African company that provides trust administration software to fiduciary firms and their
        clients. We act as the <strong>operator</strong> of personal information in terms of the
        Protection of Personal Information Act 4 of 2013 (<strong>POPIA</strong>); your trust
        specialist firm acts as the <strong>responsible party</strong> for the personal information of
        its clients.
      </p>
      <p>
        This policy describes how Elreign, as operator, handles personal information on behalf of
        responsible parties, and how we handle personal information we collect in our own right
        (such as account and billing data).
      </p>

      <h2 id="what-data">What personal information we hold</h2>
      <p>Depending on your role, we hold some or all of the following:</p>
      <ul>
        <li>
          <strong>Identity information</strong>: full name, South African ID number or passport
          number, date of birth, country of residence.
        </li>
        <li>
          <strong>Contact details</strong>: email address, telephone number, postal address.
        </li>
        <li>
          <strong>Trust relationship data</strong>: your role in a trust (founder, trustee,
          beneficiary), trust deed details, resolution history, distribution records.
        </li>
        <li>
          <strong>FICA documentation</strong>: copies of identity documents, proof of address,
          and beneficial ownership records as required by the Financial Intelligence Centre Act.
        </li>
        <li>
          <strong>Authentication data</strong>: email address, hashed password or magic-link
          tokens, TOTP secrets (stored encrypted), audit logs of sign-in events.
        </li>
        <li>
          <strong>Usage data</strong>: actions taken within the platform (resolution signings,
          document uploads, messages sent). This data forms part of the immutable audit trail.
        </li>
      </ul>
      <p>We do not collect behavioural analytics or third-party tracking data.</p>

      <h2 id="why-we-hold">Why we hold it</h2>
      <table className="legal-table">
        <thead>
          <tr>
            <th>Category</th>
            <th>Legal basis</th>
            <th>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Identity + FICA documents</td>
            <td>Legal obligation (FICA s21)</td>
            <td>Verify beneficial owners; comply with anti-money-laundering requirements.</td>
          </tr>
          <tr>
            <td>Trust relationship data</td>
            <td>Contract performance</td>
            <td>Administer resolutions, distributions, and trust deed records on behalf of the responsible party.</td>
          </tr>
          <tr>
            <td>Authentication data</td>
            <td>Contract performance / legitimate interest</td>
            <td>Secure account access; prevent unauthorised signing events.</td>
          </tr>
          <tr>
            <td>Audit trail</td>
            <td>Legal obligation (FICA s23; Trust Property Control Act)</td>
            <td>Maintain an immutable record of all trust actions for the retention period required by law.</td>
          </tr>
        </tbody>
      </table>

      <h2 id="retention">How long we keep it</h2>
      <p>
        Trust administration records, including all resolutions, FICA documents, and the full audit
        trail, are retained for a minimum of <strong>five years</strong> from the date of the last
        relevant transaction, in accordance with section 23 of the Financial Intelligence Centre Act.
        Where the trust itself remains active, records are retained for the life of the trust plus
        five years.
      </p>
      <p>
        Account data for users who have closed their accounts is deleted within 90 days, except
        where retention is required by law or where the data forms part of a trust audit trail.
      </p>
      <p>
        Backup copies are maintained in Azure-managed geo-redundant storage in the South Africa
        North region and are subject to the same retention schedule.
      </p>

      <h2 id="your-rights">Your rights under POPIA</h2>
      <p>Section 24 of POPIA gives you the following rights in respect of your personal information:</p>
      <ul>
        <li>
          <strong>Access</strong>: request a copy of the personal information we hold about you.
        </li>
        <li>
          <strong>Correction</strong>: request that inaccurate or incomplete information be
          corrected.
        </li>
        <li>
          <strong>Deletion</strong>: request deletion of personal information we no longer have
          a lawful basis to hold. Note that deletion may not be possible for records subject to
          mandatory retention under FICA s23.
        </li>
        <li>
          <strong>Portability</strong>: request your personal information in a structured,
          machine-readable format.
        </li>
        <li>
          <strong>Objection</strong>: object to processing based on legitimate interest.
        </li>
      </ul>
      <p>
        To exercise any of these rights, contact our Information Officer at the details below.
        We will respond within 30 days. Where a request is refused, we will explain why and advise
        you of your right to escalate to the Information Regulator.
      </p>

      <h2 id="information-officer">Information Officer</h2>
      <div className="legal-info-box">
        <div className="eyebrow">Contact</div>
        <p style={{ margin: 0 }}>
          Information Officer: {ioName}<br />
          Email:{' '}
          <a href={`mailto:${ioEmail}`} style={{ color: 'var(--primary-600)' }}>
            {ioEmail}
          </a>
          <br />
          Postal address: Cape Town, South Africa (full address TBD on registration)
        </p>
      </div>

      <h2 id="complaints">Complaints to the Information Regulator</h2>
      <p>
        If you are not satisfied with our response to a privacy request, you may lodge a complaint
        with the Information Regulator of South Africa:
      </p>
      <div className="legal-info-box">
        <p style={{ margin: 0 }}>
          Information Regulator (South Africa)<br />
          33 Hoofd Street, Forum III, 3rd Floor Braampark, Johannesburg, 2001<br />
          <a href="mailto:complaints.IR@justice.gov.za" style={{ color: 'var(--primary-600)' }}>
            complaints.IR@justice.gov.za
          </a>
          <br />
          <a href="https://www.justice.gov.za/inforeg" style={{ color: 'var(--primary-600)' }}>
            www.justice.gov.za/inforeg
          </a>
        </p>
      </div>
    </LegalLayout>
  );
}
